import { FC, useState, useMemo, useEffect } from 'react';
import { Modal, Form, InputNumber, Select, Row, Col, Button, Space, Typography } from 'antd';
import type { RateCard, RateCardInput } from '../EU6Calculator.types';
import { CURRENCY_OPTIONS, getCurrencySymbol } from '@/constants/currency';
import { Currency } from '../EU6Calculator.types';
import { useGetCalculatorRateCardsQuery, useGetDspOptionsQuery } from '@/api';
import { formatPercentInput, parseNumericInput, type NumericInputValue } from '@/utils/inputNumber';

interface AddRateModalProps {
  open: boolean;
  onClose: () => void;
  onAdd: (rateCard: RateCardInput) => void;
  /** When provided, modal switches into edit mode for this row. */
  initialRateCard?: RateCard | null;
  onEdit?: (rateCard: RateCardInput) => void;
  defaultCurrency: Currency;
  /** Sum of delivery percentages for all other rows (excluding the one being added/edited). */
  otherDeliveryPercentageTotal: number;
  /** Max allowed delivery % for the row being added/edited to keep total <= 100%. */
  maxDeliveryPercentage: number;
  /** Total package budget — enables the budget amount input field. */
  totalBudget?: number;
}

interface FormValues {
  country: string;
  channel: string;
  publisher: string;
  format: string;
  rowCurrency: Currency;
  buyCPM: number;
  deliveryPercentage: number;
  /** Selected DSP name from /channels/api/v1/dsp-options */
  dspFeeName: string | null;
  dspFeePercentage: number;
}

export const AddRateModal: FC<AddRateModalProps> = ({
  open,
  onClose,
  onAdd,
  onEdit,
  initialRateCard,
  defaultCurrency,
  otherDeliveryPercentageTotal,
  maxDeliveryPercentage,
  totalBudget
}) => {
  const [form] = Form.useForm<FormValues>();
  const [deliveryPercentage, setDeliveryPercentage] = useState<number>(100);
  const [budgetAmount, setBudgetAmount] = useState<number | null>(null);
  const isEditMode = open && !!initialRateCard && !!onEdit;

  const hasTotalBudget = typeof totalBudget === 'number' && totalBudget > 0;

  const applyDeliveryPercentage = (pct: number) => {
    const maxDelivery = Math.max(0, maxDeliveryPercentage);
    const clamped = Math.max(0, Math.min(maxDelivery, pct));
    setDeliveryPercentage(clamped);
    form.setFieldsValue({ deliveryPercentage: clamped });
    if (hasTotalBudget) {
      setBudgetAmount(parseFloat(((clamped / 100) * totalBudget!).toFixed(2)));
    }
  };

  const handleDeliveryChange = (value: number | null) => {
    const next = typeof value === 'number' && Number.isFinite(value) ? value : 0;
    applyDeliveryPercentage(next);
  };

  const handleBudgetChange = (value: number | null) => {
    if (!hasTotalBudget) return;
    const amount = typeof value === 'number' && Number.isFinite(value) ? value : 0;
    const maxBudget = (maxDeliveryPercentage / 100) * totalBudget!;
    const clampedAmount = Math.max(0, Math.min(maxBudget, amount));
    setBudgetAmount(clampedAmount);
    const rawPct = (clampedAmount / totalBudget!) * 100;
    applyDeliveryPercentage(rawPct);
  };

  // Watch form values for cascading dropdowns
  const selectedCountry = Form.useWatch('country', form);
  const selectedChannel = Form.useWatch('channel', form);
  const selectedPublisher = Form.useWatch('publisher', form);
  const budgetCurrencySymbol = getCurrencySymbol(defaultCurrency);

  // Fetch all rate cards to get countries
  const { data: allRateCardsData, isLoading: areCountriesLoading } = useGetCalculatorRateCardsQuery({});

  // Fetch rate cards for selected country
  const { data: countryRateCardsData, isLoading: areRateCardsLoading } = useGetCalculatorRateCardsQuery(
    { country: selectedCountry },
    { skip: !selectedCountry }
  );

  // Fetch DSP options (non-admin endpoint)
  const { data: dspOptionsData, isLoading: areDspOptionsLoading } = useGetDspOptionsQuery();

  // Extract unique countries from all rate cards
  const countryOptions = useMemo(() => {
    if (!allRateCardsData) return [];
    const uniqueCountries = Array.from(new Set(allRateCardsData.rateCards.map((rc) => rc.country)));
    return uniqueCountries.sort().map((country) => ({ value: country, label: country }));
  }, [allRateCardsData]);

  // Extract unique channels from rate cards for selected country
  const channelOptions = useMemo(() => {
    if (!countryRateCardsData) return [];
    const uniqueChannels = Array.from(new Set(countryRateCardsData.rateCards.map((rc) => rc.channel)));
    return uniqueChannels.sort().map((channel) => ({ value: channel, label: channel }));
  }, [countryRateCardsData]);

  // Extract unique publishers from rate cards for selected country and channel
  const publisherOptions = useMemo(() => {
    if (!countryRateCardsData || !selectedChannel) return [];
    const filteredCards = countryRateCardsData.rateCards.filter((rc) => rc.channel === selectedChannel);
    const uniquePublishers = Array.from(new Set(filteredCards.map((rc) => rc.publisher)));
    return uniquePublishers.sort().map((publisher) => ({ value: publisher, label: publisher }));
  }, [countryRateCardsData, selectedChannel]);

  // Extract unique formats from rate cards for selected country, channel, and publisher
  const formatOptions = useMemo(() => {
    if (!countryRateCardsData || !selectedChannel || !selectedPublisher) return [];
    const filteredCards = countryRateCardsData.rateCards.filter(
      (rc) => rc.channel === selectedChannel && rc.publisher === selectedPublisher
    );
    const uniqueFormats = Array.from(new Set(filteredCards.map((rc) => rc.format).filter(Boolean)));
    return uniqueFormats.sort().map((format) => ({ value: format, label: format }));
  }, [countryRateCardsData, selectedChannel, selectedPublisher]);

  const handleDspFeeChange = (dspName: string) => {
    const selectedDsp = dspOptionsData?.dsps?.find((d) => d.dspName === dspName);
    form.setFieldsValue({ dspFeePercentage: selectedDsp?.feePercentage ?? 0 });
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const selectedDsp = dspOptionsData?.dsps?.find((d) => d.dspName === values.dspFeeName);

      const newRate = {
        country: values.country,
        channel: values.channel,
        publisher: values.publisher,
        format: values.format,
        deliveryPercentage: values.deliveryPercentage,
        rowCurrency: values.rowCurrency,
        buyCPM: values.buyCPM,
        dspFeeId: null,
        dspFeeName: values.dspFeeName ?? selectedDsp?.dspName ?? '',
        dspFeePercentage: values.dspFeePercentage ?? selectedDsp?.feePercentage ?? 0
      };

      if (isEditMode) {
        onEdit?.(newRate);
      } else {
        onAdd(newRate);
      }
      form.resetFields();
      setDeliveryPercentage(100);
      setBudgetAmount(null);
      onClose();
    } catch (error) {
      // Validation failed
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setDeliveryPercentage(100);
    setBudgetAmount(null);
    onClose();
  };

  // Reset dependent fields when parent field changes
  const handleCountryChange = () => {
    form.setFieldsValue({
      channel: undefined,
      publisher: undefined,
      format: undefined,
      buyCPM: undefined,
      rowCurrency: defaultCurrency
    });
  };

  const handleChannelChange = () => {
    form.setFieldsValue({
      publisher: undefined,
      format: undefined,
      buyCPM: undefined,
      rowCurrency: defaultCurrency
    });
  };

  const handlePublisherChange = () => {
    form.setFieldsValue({
      format: undefined,
      buyCPM: undefined,
      rowCurrency: defaultCurrency
    });
  };

  useEffect(() => {
    if (!open) return;

    if (initialRateCard) {
      const next = Math.max(0, Math.min(maxDeliveryPercentage, initialRateCard.deliveryPercentage));
      setDeliveryPercentage(next);
      if (hasTotalBudget) setBudgetAmount(parseFloat(((next / 100) * totalBudget!).toFixed(2)));
      form.setFieldsValue({
        country: initialRateCard.country,
        channel: initialRateCard.channel,
        publisher: initialRateCard.publisher,
        format: initialRateCard.format,
        deliveryPercentage: next,
        rowCurrency: initialRateCard.rowCurrency,
        buyCPM: initialRateCard.buyCPM,
        dspFeeName: initialRateCard.dspFeeName || null,
        dspFeePercentage: initialRateCard.dspFeePercentage
      });
    } else {
      const next = Math.max(0, Math.min(100, maxDeliveryPercentage));
      setDeliveryPercentage(next);
      if (hasTotalBudget) setBudgetAmount(parseFloat(((next / 100) * totalBudget!).toFixed(2)));
      form.setFieldsValue({
        deliveryPercentage: next,
        dspFeePercentage: 0,
        dspFeeName: null,
        rowCurrency: defaultCurrency
      });
    }
  }, [open, initialRateCard, defaultCurrency, form, maxDeliveryPercentage]);

  const totalAfterThisLine = otherDeliveryPercentageTotal + deliveryPercentage;
  const remainingAfterThisLine = Math.max(0, 100 - totalAfterThisLine);

  const handleFormatChange = (formatValue: string) => {
    // Find the matching rate card
    if (countryRateCardsData && selectedChannel && selectedPublisher) {
      const matchingCard = countryRateCardsData.rateCards.find(
        (rc) =>
          rc.channel === selectedChannel && rc.publisher === selectedPublisher && rc.format === formatValue
      );

      if (matchingCard) {
        // Auto-populate buyCPM and currency
        form.setFieldsValue({
          buyCPM: matchingCard.buyCpm,
          rowCurrency: matchingCard.currency as Currency
        });
      }
    }
  };

  return (
    <Modal
      title={isEditMode ? 'Edit Rate' : 'Add Rate to Plan'}
      open={open}
      onCancel={handleCancel}
      width={600}
      footer={[
        <Button key="cancel" onClick={handleCancel}>
          Cancel
        </Button>,
        <Button key="submit" type="primary" onClick={handleSubmit}>
          {isEditMode ? 'Save Changes' : 'Add Rate'}
        </Button>
      ]}
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          deliveryPercentage: 100,
          dspFeePercentage: 0,
          dspFeeName: null,
          rowCurrency: defaultCurrency
        }}
      >
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label="Country"
              name="country"
              rules={[{ required: true, message: 'Please select a country' }]}
            >
              <Select
                placeholder="Select country"
                showSearch
                loading={areCountriesLoading}
                options={countryOptions}
                optionFilterProp="label"
                onChange={handleCountryChange}
              />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              label="Channel"
              name="channel"
              rules={[{ required: true, message: 'Please select a channel' }]}
            >
              <Select
                placeholder="Select channel"
                showSearch
                disabled={!selectedCountry}
                loading={areRateCardsLoading}
                options={channelOptions}
                optionFilterProp="label"
                onChange={handleChannelChange}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label="Publisher"
              name="publisher"
              rules={[{ required: true, message: 'Please select a publisher' }]}
            >
              <Select
                placeholder="Select publisher"
                showSearch
                disabled={!selectedCountry || !selectedChannel}
                loading={areRateCardsLoading}
                options={publisherOptions}
                optionFilterProp="label"
                onChange={handlePublisherChange}
              />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              label="Format"
              name="format"
              rules={[{ required: true, message: 'Please select a format' }]}
            >
              <Select
                placeholder="Select format"
                showSearch
                disabled={!selectedCountry || !selectedChannel || !selectedPublisher}
                loading={areRateCardsLoading}
                options={formatOptions}
                optionFilterProp="label"
                onChange={handleFormatChange}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={8}>
            <Form.Item
              label="Row Currency"
              name="rowCurrency"
              rules={[{ required: true, message: 'Please select currency' }]}
            >
              <Select
                placeholder="Select currency"
                disabled
                options={CURRENCY_OPTIONS}
              />
            </Form.Item>
          </Col>

          <Col span={8}>
            <Form.Item
              label="Buy CPM"
              name="buyCPM"
              rules={[{ required: true, message: 'Please enter buy CPM' }]}
            >
              <InputNumber style={{ width: '100%' }} placeholder="0.00" min={0} step={0.01} disabled />
            </Form.Item>
          </Col>

          <Col span={8}>
            <Form.Item
              label="DSP"
              name="dspFeeName"
              rules={[{ required: true, message: 'Please select a DSP' }]}
            >
              <Select
                placeholder="Select DSP"
                showSearch
                loading={areDspOptionsLoading}
                optionFilterProp="label"
                onChange={handleDspFeeChange}
                options={
                  dspOptionsData?.dsps?.map((d) => ({
                    label: `${d.dspName} (${d.feePercentage}%)`,
                    value: d.dspName
                  })) ?? []
                }
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label="DSP Fee %"
              name="dspFeePercentage"
              rules={[{ required: true, message: 'Please enter DSP fee percentage' }]}
            >
              <InputNumber<NumericInputValue>
                style={{ width: '100%' }}
                placeholder="10"
                min={0}
                max={100}
                step={0.1}
                formatter={formatPercentInput}
                parser={parseNumericInput}
              />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          label="Delivery"
          name="deliveryPercentage"
          rules={[{ required: true, message: 'Please set delivery percentage' }]}
        >
          <Space direction="vertical" size={8} style={{ width: '100%' }}>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              <div>Delivery attributed: {Math.min(100, totalAfterThisLine).toFixed(2)}%</div>
              <div>Remaining delivery to be attributed: {remainingAfterThisLine.toFixed(2)}%</div>
            </Typography.Text>
            <Row gutter={8}>
              <Col span={hasTotalBudget ? 12 : 24}>
                <InputNumber
                  min={0}
                  max={Math.max(0, maxDeliveryPercentage)}
                  step={0.01}
                  precision={2}
                  value={deliveryPercentage}
                  onChange={(v) => handleDeliveryChange(v)}
                  addonAfter={<span style={{ color: '#ffffff' }}>%</span>}
                  style={{ width: '100%' }}
                />
              </Col>
              {hasTotalBudget && (
                <Col span={12}>
                  <InputNumber
                    min={0}
                    max={parseFloat(((maxDeliveryPercentage / 100) * totalBudget!).toFixed(2))}
                    step={1}
                    precision={2}
                    value={budgetAmount}
                    onChange={(v) => handleBudgetChange(v)}
                    addonBefore={<span style={{ color: '#ffffff' }}>{budgetCurrencySymbol}</span>}
                    style={{ width: '100%' }}
                    placeholder="Budget amount"
                  />
                </Col>
              )}
            </Row>
          </Space>
        </Form.Item>
      </Form>
    </Modal>
  );
};
